import type { Prisma } from "@prisma/client";
import { ProjectCategory } from "@remi/enums/project-category";
import { isOneOf } from "@remi/ts-utils";
import { type AssignableProjectRole, ProjectRole } from "@remi/types/projectRole";

import { hasCapability, type User } from "../base";
import { NON_EXISTENT_NUMERIC_ID } from "../utils/types";

const CONTRACTOR_ASSIGNABLE_PROJECT_ROLES = [ProjectRole.CONTRACTOR_PRODUCTION, ProjectRole.FIELD_WORKER] as const;

export const projectTable = {
  read: (user: User) => hasCapability(user, "projectTable.read"),
};

export const projectStatus = {
  update: (user: User) => hasCapability(user, "projectStatus.update"),
  alwaysUpdate: (user: User) => hasCapability(user, "projectStatus.alwaysUpdate"),
  alwaysCancel: (user: User) => hasCapability(user, "projectStatus.alwaysCancel"),
};

export const project = {
  read: (user: User) => hasCapability(user, "project.read"),
  create: (user: User) => hasCapability(user, "project.create"),
  update: (user: User) => hasCapability(user, "project.update"),
  delete: (user: User) => hasCapability(user, "project.delete"),
  reevaluate: (user: User) => hasCapability(user, "project.reevaluate"),
  updateEstimateDate: (user: User) => hasCapability(user, "project.updateEstimateDate"),
  photos: (user: User) => hasCapability(user, "project.photos"),
  corrections: {
    create: (user: User) => hasCapability(user, "project.corrections.create"),
    createWarrantyBid: (user: User) => hasCapability(user, "project.corrections.createWarrantyBid"),
  },
  solarSiteAssessment: {
    create: (user: User) => hasCapability(user, "project.solarSiteAssessment.create"),
  },
  reinspection: {
    create: (user: User) => hasCapability(user, "project.reinspection.create"),
  },
  qa: {
    create: (user: User) => hasCapability(user, "project.qa.create"),
    assess: (user: User) => hasCapability(user, "project.qa.assess"),
  },
  owner: {
    update: (user: User, role?: AssignableProjectRole) => {
      if (isOneOf(role, CONTRACTOR_ASSIGNABLE_PROJECT_ROLES)) {
        return hasCapability(user, "project.owner.updateContractorRole") || hasCapability(user, "project.owner.update");
      }
      return hasCapability(user, "project.owner.update");
    },
    updateAccountManager: (user: User) => hasCapability(user, "project.owner.updateAccountManager"),
    updateAdjuster: (user: User) => hasCapability(user, "project.owner.updateAdjuster"),
  },
};

/**
 * The participation scope both external read capabilities share, with QA excluded.
 *
 * A QA visit is an internal assessment of a contractor's finished work, created on the *covered bid's*
 * workSite — so `workSite.homeownerId` alone hands it to the homeowner, and the identical disjunct under
 * `project.read.buyer` hands it to a partner buyer. Narrowing one and not the other would leave the leak
 * reachable through the capability that was missed, so both go through here.
 *
 * The assignment scopes are deliberately NOT narrowed: a Remi field worker dispatched to perform the
 * visit reads it through `project.read.assigned`, which is an assignment grant rather than a
 * participation one.
 */
const getExternalParticipationScope = (user: User): Prisma.ProjectWhereInput => ({
  AND: [
    { category: { not: ProjectCategory.QA } },
    {
      OR: [
        { organizations: { some: { organizationId: user.person.organizationId, deletedAt: null } } },
        { workSite: { homeownerId: user.person.id } },
      ],
    },
  ],
});

// No Prisma.ProjectWhereInput return annotation — callers spread this into
// `findUnique({ where: { id, ...projectWhere(user) } })` which expects
// ProjectWhereUniqueInput. The inferred union-less return stays compatible.
export const projectWhere = (user: User) => {
  if (hasCapability(user, "project.read.all")) {
    return {};
  }

  const clauses: Prisma.ProjectWhereInput[] = [];

  if (hasCapability(user, "project.read.org")) {
    clauses.push(getExternalParticipationScope(user));
  }
  // Not org-scoped: crews are routinely assigned across orgs, so the assignment is the grant.
  // Must stay in step with the owners branch of `fieldUserAccessWhere` (projectCheckInDao) — a
  // narrower gate means the mobile list shows a job whose every detail call then 403s (NSYNC-7522).
  // Its check-in branch is deliberately not mirrored: a geofence ping is presence, not assignment.
  if (hasCapability(user, "project.read.assigned.production")) {
    clauses.push({
      owners: { some: { personId: user.person.id, role: ProjectRole.CONTRACTOR_PRODUCTION, deletedAt: null } },
    });
  }
  if (hasCapability(user, "project.read.assigned")) {
    clauses.push({ owners: { some: { personId: user.person.id, role: ProjectRole.FIELD_WORKER, deletedAt: null } } });
  }
  if (hasCapability(user, "project.read.buyer")) {
    // Buyer scope: projects where user's org is a participant OR user is the
    // homeowner. Mirrors the `allOrganizationsWhere` shape the pre-migration
    // silent fallback emitted for BUYER_ADMIN.
    clauses.push(getExternalParticipationScope(user));
  }

  if (clauses.length === 0) {
    return { AND: [{ id: NON_EXISTENT_NUMERIC_ID }] };
  }
  return { OR: clauses };
};
